<script setup lang="ts">
  import { ref, watch, onUnmounted, onMounted } from 'vue'
  import { Play, Pause, Loader2 } from 'lucide-vue-next'

  interface Props {
    audioUrl: string
    startTime: string
    endTime: string
  }

  const props = defineProps<Props>()
  const audioRef = ref<HTMLAudioElement | null>(null)
  const timeoutId = ref<number | null>(null)
  const isPlaying = ref(false)
  const isLoading = ref(true)
  const proxyAudioUrl = ref(props.audioUrl)

  const toggleAudio = () => {
    if (!audioRef.value) return
    if (isPlaying.value) {
      audioRef.value.pause()
    } else {
      playAudio()
    }
  }

  const playAudio = () => {
    if (!audioRef.value) return
    const startSeconds = Math.max(0, parseFloat(props.startTime) - 2)
    const endSeconds = parseFloat(props.endTime) + 2
    audioRef.value.currentTime = startSeconds
    audioRef.value.play()

    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }

    const duration = (endSeconds - startSeconds) * 1000
    timeoutId.value = window.setTimeout(() => {
      audioRef.value?.pause()
    }, duration)
  }

  const handlePlay = () => {
    isPlaying.value = true
  }

  const handlePause = () => {
    isPlaying.value = false
  }

  const handleError = (event: Event) => {
    console.error('Audio loading error:', event)
  }

  watch(
    () => props.audioUrl,
    (newUrl) => {
      if (audioRef.value && proxyAudioUrl.value !== newUrl) {
        proxyAudioUrl.value = newUrl
        audioRef.value.load()
        isLoading.value = true
      }
    }
  )

  onMounted(() => {
    if (audioRef.value) {
      audioRef.value.addEventListener('loadeddata', () => {
        isLoading.value = false
      })
    }
  })

  onUnmounted(() => {
    if (audioRef.value) {
      audioRef.value.pause()
      audioRef.value = null
    }
    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }
  })
</script>

<template>
  <button
    data-cy="audioPlaybackButton"
    :data-audio-url="audioUrl"
    :data-start-time="startTime"
    :data-end-time="endTime"
    class="inline-flex h-10 w-10 items-center justify-center rounded-md bg-purple-50 text-purple-600
      transition-colors hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500
      focus:ring-offset-2"
    @click="toggleAudio"
  >
    <Loader2
      v-if="isLoading"
      class="h-4 w-4 animate-spin"
    />
    <Pause
      v-else-if="isPlaying"
      class="h-4 w-4"
    />
    <Play
      v-else
      class="h-4 w-4"
    />
  </button>

  <audio
    ref="audioRef"
    preload="auto"
    @play="handlePlay"
    @pause="handlePause"
    @ended="handlePause"
    @error="handleError"
  >
    <source
      :src="proxyAudioUrl"
      type="audio/wav"
    />
  </audio>
</template>
